import { useEffect, useState } from 'react';
import { Eye, EyeOff, Check } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, LoadingState, ErrorState, inputClass } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/lib/auth';
import api, { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Account {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
}

function parseError(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message);
      if (parsed.error) return parsed.error;
    } catch {
      /* ignore */
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

export function AccountPage() {
  const { data, loading, error } = useApi<{ account: Account }>('/profile/account');
  const { refreshUser } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (data?.account) {
      setDisplayName(data.account.displayName ?? '');
      setUsername(data.account.username);
      setEmail(data.account.email);
    }
  }, [data]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const isPasswordValid = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileError(null);
    try {
      await api.patch('/profile/account', { displayName, username, email });
      await refreshUser();
      showToast('Changes saved');
    } catch (err) {
      setProfileError(parseError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    if (!currentPassword || !isPasswordValid || !passwordsMatch) return;
    setSavingPassword(true);
    setPasswordError(null);
    try {
      await api.patch('/profile/account', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('Password updated');
    } catch (err) {
      setPasswordError(parseError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Page>
      <PageHeader section="Account Details" backHref="/profile" backLabel="Profile" />

      <Main className="space-y-6">
        {toast && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg">
            <Check className="w-4 h-4" />
            <span className="text-sm font-medium">{toast}</span>
          </div>
        )}

        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && data && (
          <>
            {/* Profile Information */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide px-1 font-display">
                Profile Information
              </h3>
              <Card className="p-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                    Username
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      className={cn(inputClass, 'pl-8')}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Only letters, numbers, and underscores</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {profileError && <p className="text-sm text-destructive">{profileError}</p>}

                <Button fullWidth onClick={handleSaveProfile} disabled={savingProfile}>
                  {savingProfile ? 'Saving…' : 'Save Profile'}
                </Button>
              </Card>
            </div>

            {/* Change Password */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide px-1 font-display">
                Change Password
              </h3>
              <Card className="p-4 space-y-4">
                <PasswordField
                  label="Current Password"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                  show={showCurrent}
                  onToggle={() => setShowCurrent((v) => !v)}
                  placeholder="Enter current password"
                />
                <div>
                  <PasswordField
                    label="New Password"
                    value={newPassword}
                    onChange={setNewPassword}
                    show={showNew}
                    onToggle={() => setShowNew((v) => !v)}
                    placeholder="Enter new password"
                  />
                  {newPassword && !isPasswordValid && (
                    <p className="text-xs text-destructive mt-1">Password must be at least 8 characters</p>
                  )}
                </div>
                <div>
                  <PasswordField
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    show={showConfirm}
                    onToggle={() => setShowConfirm((v) => !v)}
                    placeholder="Confirm new password"
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-xs text-destructive mt-1">Passwords do not match</p>
                  )}
                  {passwordsMatch && isPasswordValid && (
                    <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <Check className="w-3 h-3" /> Passwords match
                    </p>
                  )}
                </div>

                {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}

                <Button
                  fullWidth
                  onClick={handleSavePassword}
                  disabled={savingPassword || !currentPassword || !isPasswordValid || !passwordsMatch}
                >
                  {savingPassword ? 'Saving…' : 'Update Password'}
                </Button>
              </Card>
            </div>
          </>
        )}
      </Main>
    </Page>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  show,
  onToggle,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  placeholder: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(inputClass, 'pr-12')}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
